import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useEscapeKey } from '../../hooks/useEscapeKey';

const ImageModal = ({ imageUrl, alt = "Preview Image", isOpen, onClose }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Add ESC key handling
  useEscapeKey(onClose, isOpen);

  // Reset loading state when modal opens with new image
  React.useEffect(() => {
    if (isOpen && imageUrl) {
      console.log('🖼️ ImageModal opening with URL:', imageUrl);
      setIsLoading(true);
      setHasError(false);
    }
  }, [isOpen, imageUrl]);

  if (!isOpen || !imageUrl) return null;

  // Handle click on backdrop to close modal
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleImageLoad = () => {
    console.log('✅ Image loaded successfully');
    setIsLoading(false);
    setHasError(false);
  };

  const handleImageError = (e) => {
    console.log('❌ Image failed to load:', e.target.src);
    setIsLoading(false);
    setHasError(true);
  };

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4 z-50"
      onClick={handleBackdropClick}
    >
      <div className="relative max-w-[95vw] max-h-[95vh] flex items-center justify-center">
        {/* Close button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10 bg-black bg-opacity-50 rounded-full p-2"
          aria-label="Close image"
        >
          <X className="h-6 w-6" />
        </button>

        {/* Loading spinner */}
        {isLoading && !hasError && (
          <div className="flex items-center justify-center text-white">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
              <p>Loading image...</p>
            </div>
          </div>
        )}

        {/* Error state */}
        {hasError && (
          <div className="text-center text-white">
            <div className="text-6xl mb-4">🖼️</div>
            <h3 className="text-xl font-semibold mb-2">Image Not Available</h3>
            <p className="text-gray-300 mb-4">Failed to load the image</p>
            <p className="text-sm text-gray-400 mb-4 break-all">{imageUrl}</p>
            <button 
              onClick={onClose}
              className="bg-white text-black px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        )}

        {/* Image */}
        <img
          src={imageUrl}
          alt={alt}
          className={`max-w-full max-h-full object-contain rounded-lg shadow-2xl transition-opacity duration-200 ${
            isLoading ? 'opacity-0 absolute' : 'opacity-100'
          }`}
          style={{ display: hasError ? 'none' : 'block' }}
          onClick={(e) => e.stopPropagation()} // Prevent closing when clicking on image
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
      </div>

      {/* Instructions text */}
      {!isLoading && !hasError && (
        <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 text-white text-sm opacity-70">
          Click outside image or press ESC to close
        </div>
      )}
    </div>
  );
};

export default ImageModal;